import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Gift, Copy, Check, Users, Share2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { referralAPI } from '../services/api';
import ShareButtons from './ShareButtons';

const ReferralProgram = ({ variant = 'full' }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({ total_referrals: 0, pending: 0, confirmed: 0, free_months_earned: 0 });

  useEffect(() => {
    if (user?.id) {
      referralAPI.getMyCode().then(res => {
        setReferralCode(res.data.referral_code);
        setReferralLink(res.data.referral_link);
      }).catch(() => {
        const fallbackCode = `SF${user.id.substring(0, 6).toUpperCase()}`;
        setReferralCode(fallbackCode);
        setReferralLink(`https://smartfacture-ai.com/register?ref=${fallbackCode}`);
      });

      referralAPI.getStats().then(res => {
        setStats(res.data);
      }).catch(() => {});
    }
  }, [user]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  if (variant === 'compact') {
    return (
      <Card className="border-slate-200 bg-gradient-to-br from-violet-50 to-indigo-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
              <Gift className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 text-sm">Parrainez, gagnez !</p>
              <p className="text-xs text-slate-600 truncate">1 mois gratuit par filleul</p>
            </div>
            <Button 
              size="sm" 
              onClick={handleCopy}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="referral-copy-compact"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'widget') {
    return (
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50 via-indigo-50 to-blue-50 overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
              <Gift className="h-4 w-4 text-white" />
            </div>
            <CardTitle className="text-base text-slate-800">Programme Parrainage</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-violet-600">1 mois</p>
                <p className="text-xs text-slate-600">gratuit pour vous</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-emerald-600">1 mois</p>
                <p className="text-xs text-slate-600">gratuit pour l'ami</p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600">Votre lien de parrainage</label>
              <div className="flex gap-2">
                <Input 
                  value={referralLink}
                  readOnly
                  className="text-xs bg-white/80 border-violet-200"
                  data-testid="referral-link-widget"
                />
                <Button 
                  size="sm"
                  onClick={handleCopy}
                  className={`${copied ? 'bg-emerald-500' : 'bg-violet-600 hover:bg-violet-700'} text-white px-3`}
                  data-testid="referral-copy-widget"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <ShareButtons 
              url={referralLink}
              title="SmartFacture AI - Facturation 2026"
              description="Préparez-vous à la réforme e-facturation 2026 ! Utilisez mon lien pour 1 mois gratuit."
              variant="compact"
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="referral-program">
      <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Gift className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Programme de Parrainage</h2>
            <p className="text-violet-100">Invitez vos contacts, gagnez des mois gratuits</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-amber-300" />
              <span className="font-semibold">Pour vous</span>
            </div>
            <p className="text-2xl font-bold">1 mois gratuit</p>
            <p className="text-sm text-violet-100">par filleul qui s'abonne</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-emerald-300" />
              <span className="font-semibold">Pour votre ami</span>
            </div>
            <p className="text-2xl font-bold">1 mois gratuit</p>
            <p className="text-sm text-violet-100">offert à l'inscription</p>
          </div>
        </div>
      </div>

      <CardContent className="p-6">
        <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-800" data-testid="referral-total">{stats.total_referrals}</p>
            <p className="text-xs text-slate-500">Filleuls</p>
          </div>
          <div className="text-center border-x border-slate-200">
            <p className="text-2xl font-bold text-amber-500" data-testid="referral-pending">{stats.pending}</p>
            <p className="text-xs text-slate-500">En attente</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-500" data-testid="referral-earned">{stats.free_months_earned}</p>
            <p className="text-xs text-slate-500">Mois gagnés</p>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <label className="text-sm font-medium text-slate-700">Votre lien de parrainage personnalisé</label>
          <div className="flex gap-2">
            <Input 
              value={referralLink}
              readOnly
              className="bg-slate-50 border-slate-200 font-mono text-sm"
              data-testid="referral-link-full"
            />
            <Button 
              onClick={handleCopy}
              className={`${copied ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'} text-white gap-2 min-w-[120px]`}
              data-testid="referral-copy-full"
            >
              {copied ? (
                <><Check className="h-4 w-4" /> Copié !</>
              ) : (
                <><Copy className="h-4 w-4" /> Copier</>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Code parrain : <span className="font-mono font-semibold" data-testid="referral-code">{referralCode}</span>
            {' '} (max {stats.max_free_months || 12} mois offerts)
          </p>
        </div>

        <div className="space-y-3">
          <label className="text-sm font-medium text-slate-700">Partager facilement</label>
          <ShareButtons 
            url={referralLink}
            title="SmartFacture AI - Facturation électronique 2026"
            description="Je prépare mon entreprise à la réforme e-facturation 2026 avec SmartFacture AI. Utilisez mon lien pour 1 mois gratuit !"
          />
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-3">Comment ça marche ?</h3>
          <ol className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              Partagez votre lien avec vos contacts
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              Ils s'inscrivent et reçoivent 1 mois gratuit
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              À leur souscription, vous recevez aussi 1 mois gratuit
            </li>
          </ol>
          <p className="text-xs text-slate-400 mt-3">
            Le mois gratuit est crédité après le premier paiement du filleul. Maximum 12 mois offerts. Non échangeable contre de l'argent.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralProgram;
